"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { X } from "lucide-react";
import Link from "next/link";
import { LazyUpgradeQuoteModal } from "@/components/billing/LazyUpgradeQuoteModal";
import { useAuth } from "@/contexts/auth-context";
import { hasEntitlement } from "@/lib/billing/entitlements";
import { safeGet, safeSet } from "@/components/onboarding/safe-storage";
import { WhaleWatchVideoModalLink } from "@/components/whale-watch-video-modal";
import { cn } from "@/lib/utils";
import type { AppPromoConfig } from "./promo-config";
import { canOpenUpgradeFlowClient } from "@/lib/billing/upgrade-flow-config";
import { planSubscriptionFromRow, routePurchase, type PlanChangeRoute } from "@/lib/billing/purchase-route";
import { TIER_MONTHLY_PRICE_ID, TIER_YEARLY_PRICE_ID } from "@/lib/billing/tier-price-ids";

const STORAGE_PREFIX = "upsider_app_promo_dismissed";

export const SHELL_BANNER_CTA = "text-[13px] whitespace-nowrap font-medium text-primary hover:text-primary/70 transition-colors inline-flex items-center gap-1";

export function AppPromoBanner({ promo }: { promo: AppPromoConfig }) {
  const { isLoading, user, subscriptions } = useAuth();
  const [shouldRender, setShouldRender] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [upgradeTarget, setUpgradeTarget] = useState<PlanChangeRoute | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const rafRef = useRef<number | undefined>(undefined);

  const storageKey = `${STORAGE_PREFIX}:${promo.id}`;
  const promoYearly =
    promo.cta.kind === "upgrade" &&
    (promo.cta.targetInterval === "year" || promo.cta.targetInterval === "yearly" || promo.cta.targetInterval === "annual");
  const upgradeRoute =
    promo.cta.kind === "upgrade"
      ? routePurchase((subscriptions ?? []).map(planSubscriptionFromRow), {
          priceId: (promoYearly ? TIER_YEARLY_PRICE_ID : TIER_MONTHLY_PRICE_ID)[promo.cta.targetTier],
          interval: promoYearly ? "year" : "month",
        })
      : null;
  const planChange = upgradeRoute?.kind === "plan_change" && canOpenUpgradeFlowClient(user) ? upgradeRoute : null;
  const modalRoute = upgradeTarget ?? planChange;

  useEffect(() => {
    if (isLoading) {
      setIsOpen(false);
      setShouldRender(false);
      return;
    }

    if (hasEntitlement(subscriptions ?? [], promo.entitlement)) {
      setIsOpen(false);
      setShouldRender(false);
      return;
    }

    if (safeGet(storageKey) !== null) {
      setIsOpen(false);
      setShouldRender(false);
      return;
    }

    setShouldRender(true);
    rafRef.current = requestAnimationFrame(() => setIsOpen(true));

    return () => {
      if (rafRef.current !== undefined) cancelAnimationFrame(rafRef.current);
    };
  }, [isLoading, subscriptions, promo.id, promo.entitlement, storageKey]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
      if (rafRef.current !== undefined) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  function handleDismiss() {
    setIsOpen(false);
    safeSet(storageKey, new Date().toISOString());
    closeTimeoutRef.current = setTimeout(() => setShouldRender(false), 200);
  }

  if (!shouldRender && !upgradeTarget) return null;

  return (
    <>
      <ShellBanner
        open={isOpen}
        label="Announcement"
        dismissLabel="Dismiss announcement"
        onDismiss={handleDismiss}
        badge={
          <span className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.15em] px-2 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/25">
            {promo.badgeText}
          </span>
        }
        strong={promo.strongText}
        rest={promo.restText}
        action={
          <PromoCta
            cta={promo.cta}
            canOpenUpgradeModal={planChange !== null}
            onOpenUpgrade={() => setUpgradeTarget(planChange)}
          />
        }
      />
      {promo.cta.kind === "upgrade" && modalRoute && (
        <LazyUpgradeQuoteModal
          open={upgradeTarget !== null}
          targetTier={modalRoute.targetTier}
          targetInterval={modalRoute.targetInterval}
          source={promo.cta.source ?? "feature_gate"}
          onClose={() => setUpgradeTarget(null)}
        />
      )}
    </>
  );
}

export function ShellBanner({
  open,
  label,
  dismissLabel,
  onDismiss,
  badge,
  strong,
  rest,
  action,
}: {
  open: boolean;
  label: string;
  dismissLabel: string;
  onDismiss: () => void;
  badge?: ReactNode;
  strong: string;
  rest?: string;
  action: ReactNode;
}) {
  return (
    <section
      className={cn(
        "flex-none overflow-hidden",
        "transition-[max-height,opacity] duration-200 ease-out motion-reduce:transition-none",
        open ? "max-h-14 opacity-100" : "max-h-0 opacity-0 pointer-events-none"
      )}
      aria-label={label}
      aria-hidden={!open}
      inert={!open}
    >
      <div className="relative border-b border-primary/10 bg-primary/[0.03]">
        <div className="absolute inset-y-0 left-0 w-[2px] bg-gradient-to-b from-primary/60 via-primary/30 to-transparent" />

        <div className="min-h-11 pl-4 pr-2 flex items-center gap-3 min-w-0">
          <div className="flex items-center gap-2.5 min-w-0">
            {badge}
            <p className="text-[13px] min-w-0 truncate">
              <span className="font-medium text-white/90">{strong}</span>
              {rest && <span className="hidden sm:inline text-white/45">{" "}{rest}</span>}
            </p>
          </div>

          <div className="ml-auto flex items-center gap-2 shrink-0">
            {action}
            <div className="w-px h-4 bg-white/10" />
            <button type="button"
              onClick={onDismiss}
              className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded text-white/30 hover:text-white/60 hover:bg-white/5 transition-colors"
              aria-label={dismissLabel}
            >
              <X className="size-3.5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function PromoCta({
  cta,
  canOpenUpgradeModal,
  onOpenUpgrade,
}: {
  cta: AppPromoConfig["cta"];
  canOpenUpgradeModal: boolean;
  onOpenUpgrade: () => void;
}) {
  if (cta.kind === "video_modal") {
    return (
      <WhaleWatchVideoModalLink
        videoUrl={cta.videoUrl}
        label={cta.label}
        className="text-[13px] whitespace-nowrap font-medium text-primary hover:text-primary/70"
      />
    );
  }

  if (cta.kind === "upgrade") {
    if (canOpenUpgradeModal) {
      return (
        <button
          type="button"
          onClick={onOpenUpgrade}
          className={SHELL_BANNER_CTA}
        >
          {cta.label}
        </button>
      );
    }

    return (
      <Link
        href={cta.href}
        className={SHELL_BANNER_CTA}
      >
        {cta.label}
      </Link>
    );
  }

  if (cta.external) {
    return (
      <a
        href={cta.href}
        target="_blank"
        rel="noopener noreferrer"
        className={SHELL_BANNER_CTA}
      >
        {cta.label}
      </a>
    );
  }

  return (
    <Link
      href={cta.href}
      className={SHELL_BANNER_CTA}
    >
      {cta.label}
    </Link>
  );
}
